
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BugReportCategory, BugReportCreateInput, BugReportLocation } from "@/hooks/useBugReports";
import { Camera, Loader2, Upload } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { BugReportScreenshots } from "./BugReportScreenshots";

const bugReportLocations: BugReportLocation[] = [
  'Contracts',
  'Contract Types',
  'Categories',
  'Currencies',
  'Exchange Rates',
  'Groups',
  'Contacts',
  'Users',
  'Admin',
  'Dashboard',
  'Profile',
  'Settings',
  'Other'
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const formSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  category: z.enum(['Bug', 'Feature', 'Hotfix']),
  location: z.string().min(1, "Please select a location"),
});

interface BugReportFormProps {
  onSubmit: (data: BugReportCreateInput) => void;
  isSubmitting: boolean;
  initialLocation?: string;
  initialScreenshots?: File[];
  onTakeScreenshot?: () => Promise<File | null>;
}

export function BugReportForm({ 
  onSubmit, 
  isSubmitting, 
  initialLocation,
  initialScreenshots = [],
  onTakeScreenshot
}: BugReportFormProps) {
  const [screenshots, setScreenshots] = useState<File[]>(initialScreenshots);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isTakingScreenshot, setIsTakingScreenshot] = useState(false);

  // Generate preview URLs from initial screenshots
  useEffect(() => {
    const urls = initialScreenshots.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
    
    // Cleanup function to revoke object URLs
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [initialScreenshots]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "Bug",
      location: initialLocation || "",
    },
  });

  // Update form values when initialLocation changes
  useEffect(() => {
    if (initialLocation) {
      form.setValue('location', initialLocation);
    }
  }, [initialLocation, form]);

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    
    const newFiles: File[] = [];
    const newPreviewUrls: string[] = [];
    
    Array.from(e.target.files).forEach(file => {
      // Validate file size and type
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File ${file.name} is too large. Max size is 5MB.`);
        return;
      }
      
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`File ${file.name} has invalid format. Accepted formats: JPEG, PNG, WebP.`);
        return;
      }
      
      newFiles.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    });
    
    setScreenshots([...screenshots, ...newFiles]);
    setPreviewUrls([...previewUrls, ...newPreviewUrls]);
  };

  const handleTakeScreenshot = async () => {
    if (!onTakeScreenshot) return;
    
    setIsTakingScreenshot(true);
    try {
      const screenshot = await onTakeScreenshot();
      if (screenshot) {
        // The BugReportButton component will handle adding the screenshot
        // and reopening the modal
      }
    } catch (error) {
      console.error("Error taking screenshot:", error);
    } finally {
      setIsTakingScreenshot(false);
    }
  };

  const removeScreenshot = (index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    const updatedScreenshots = [...screenshots];
    const updatedPreviewUrls = [...previewUrls];
    updatedScreenshots.splice(index, 1);
    updatedPreviewUrls.splice(index, 1);
    setScreenshots(updatedScreenshots);
    setPreviewUrls(updatedPreviewUrls);
  };

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onSubmit({
      title: values.title,
      description: values.description,
      category: values.category as BugReportCategory,
      location: values.location,
      screenshots: screenshots
    });
  };

  return (
    <Card className="border rounded-xl shadow-lg bg-white/90 backdrop-blur-sm dark:bg-gray-800/90 transition-all duration-300 hover:shadow-xl">
      <CardContent className="p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-base font-medium">Title</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Brief summary of the issue" 
                      {...field} 
                      className="px-4 py-3 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/50 transition-all" 
                    />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-base font-medium">Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Detailed explanation of the issue or improvement" 
                      className="min-h-[140px] px-4 py-3 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/50 resize-y transition-all"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-base font-medium">Category</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="px-4 py-3 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/50 transition-all">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border-0">
                        <SelectItem value="Bug" className="py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">Bug</SelectItem>
                        <SelectItem value="Feature" className="py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">Feature</SelectItem>
                        <SelectItem value="Hotfix" className="py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">Hotfix</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-base font-medium">Location</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="px-4 py-3 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/50 transition-all">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border-0 max-h-80">
                        {bugReportLocations.map((location) => (
                          <SelectItem 
                            key={location} 
                            value={location}
                            className="py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            {location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <FormLabel className="text-base font-medium">Screenshots</FormLabel>
              <div className="flex flex-wrap items-center gap-3">
                {onTakeScreenshot && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleTakeScreenshot}
                    disabled={isTakingScreenshot}
                    className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 transition-all duration-200"
                  >
                    {isTakingScreenshot ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Take Screenshot
                  </Button>
                )}
                
                <label 
                  htmlFor="screenshots" 
                  className="flex items-center gap-2 px-4 py-3 rounded-md border border-input hover:bg-muted cursor-pointer transition-colors duration-200"
                >
                  <Upload size={18} />
                  <span>Upload images</span>
                </label>
                <Input 
                  id="screenshots" 
                  type="file" 
                  accept="image/png, image/jpeg, image/webp" 
                  multiple
                  className="hidden"
                  onChange={handleScreenshotChange}
                />
                <p className="text-sm text-muted-foreground">
                  Max 5 images (JPG, PNG, WebP), up to 5MB each
                </p>
              </div>
              
              {previewUrls.length > 0 && (
                <BugReportScreenshots screenshots={previewUrls} onRemove={removeScreenshot} />
              )}
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={isSubmitting} 
                className="px-6 py-3 text-base bg-primary hover:bg-primary/90 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
